import React, { useState, useEffect } from 'react'
import { useDispatch } from 'react-redux'
import { HitApi } from '../../../Api/ApiHit'
import {
    searchAppSectionClient,
    deleteAppSectionClient,
    toggleSectionVisibility,
    reorderSections,
    searchGroup
} from '../../../constant/Constant'
import { getSelectedUnit } from '../../../storage/Storage'
import toast from 'react-hot-toast'
import {
    Loader2, Plus, Trash2, Pencil, GripVertical, Eye, EyeOff,
    LayoutGrid, List, ArrowLeftRight
} from 'lucide-react'
import SectionModal from './SectionModal'

const SectionsManager = ({ onSectionsChange, projectId }) => {
    const [sections, setSections] = useState([])
    const [loading, setLoading] = useState(false)
    const [showModal, setShowModal] = useState(false)
    const [editingSection, setEditingSection] = useState(null)
    const [parentServices, setParentServices] = useState([])
    const [dragIndex, setDragIndex] = useState(null)
    const [dragOverIndex, setDragOverIndex] = useState(null)
    const [editLoading, setEditLoading] = useState(null)
    const [serviceType, setServiceType] = useState('')
    const [serviceTypes, setServiceTypes] = useState([])
    const [serviceTypesLoading, setServiceTypesLoading] = useState(true)

    const unitId = getSelectedUnit()?._id || getSelectedUnit()?.[0]

    useEffect(() => {
        setServiceType('')
        setSections([])
        fetchServiceTypes()
        fetchParentServices()
    }, [unitId, projectId])

    useEffect(() => {
        if (projectId && serviceType) {
            fetchSections()
        } else {
            setSections([])
            setLoading(false)
        }
    }, [unitId, serviceType, projectId])

    const fetchSections = async () => {
        if (!unitId || !projectId || !serviceType) return
        setLoading(true)
        try {
            const json = {
                page: 1,
                limit: 100,
                search: { unitIds: unitId, projectId, serviceType },
                sort: { order: 1 }
            }
            const res = await HitApi(json, searchAppSectionClient)
            if (res?.data?.docs) {
                setSections(res.data.docs)
            }
        } catch (error) {
            toast.error('Failed to fetch sections')
        }
        setLoading(false)
    }

    const fetchParentServices = async () => {
        if (!unitId) return
        try {
            const json = {
                page: 1,
                limit: 100000,
                unitIds: unitId
            }
            const res = await HitApi(json, searchGroup)
            if (res?.data?.docs) {
                setParentServices(res.data.docs)
            }
        } catch (error) {
            console.error('Failed to fetch groups')
        }
    }

    const fetchServiceTypes = async () => {
        if (!unitId || !projectId) {
            setServiceTypesLoading(false)
            return
        }
        setServiceTypesLoading(true)
        try {
            const json = {
                page: 1,
                limit: 1000,
                search: { unitIds: unitId, projectId },
                sort: { order: 1 }
            }
            const res = await HitApi(json, searchAppSectionClient)
            if (res?.data?.docs) {
                // Extract unique service types from sections
                const uniqueTypes = [...new Set(res.data.docs.map(s => s.serviceType).filter(Boolean))]
                setServiceTypes(uniqueTypes)
                // Auto-select first service type if available
                if (uniqueTypes.length > 0) {
                    setServiceType(uniqueTypes[0])
                }
            }
        } catch (error) {
            console.error('Failed to fetch service types')
        }
        setServiceTypesLoading(false)
    }

    const handleAddSection = () => {
        setEditingSection(null)
        setShowModal(true)
    }

    const handleEditSection = async (section) => {
        setEditLoading(section._id)
        try {
            const res = await HitApi({
                page: 1,
                limit: 1,
                search: { _id: section._id, unitIds: unitId }
            }, searchAppSectionClient)
            const fetched = res?.data?.docs?.[0]
            const fullSection = fetched
                ? {
                    ...fetched,
                    services: fetched.services?.length > 0 ? fetched.services : (section.services || []),
                    categories: fetched.categories?.length > 0 ? fetched.categories : (section.categories || [])
                  }
                : section
            setEditingSection(fullSection)
        } catch {
            setEditingSection(section)
        }
        setEditLoading(null)
        setShowModal(true)
    }

    const handleDeleteSection = async (section) => {
        if (!window.confirm(`Delete section "${section.title}"? This cannot be undone.`)) return
        try {
            const res = await HitApi({ _id: section._id }, deleteAppSectionClient)
            if (res?.success) {
                toast.success('Section deleted')
                fetchSections()
                onSectionsChange?.()
            } else {
                toast.error(res?.message || 'Failed to delete section')
            }
        } catch (error) {
            toast.error('Failed to delete section')
        }
    }

    const handleToggleVisibility = async (section) => {
        try {
            const res = await HitApi({ _id: section._id, isActive: !section.isActive }, toggleSectionVisibility)
            if (res?.success) {
                toast.success(section.isActive ? 'Section hidden' : 'Section visible')
                fetchSections()
                onSectionsChange?.()
            } else {
                toast.error('Failed to toggle visibility')
            }
        } catch (error) {
            toast.error('Failed to toggle visibility')
        }
    }

    const handleDragStart = (e, index) => {
        setDragIndex(index)
        e.dataTransfer.effectAllowed = 'move'
    }

    const handleDragOver = (e, index) => {
        e.preventDefault()
        e.dataTransfer.dropEffect = 'move'
        if (dragOverIndex !== index) {
            setDragOverIndex(index)
        }
    }

    const handleDragEnd = async () => {
        if (dragIndex === null || dragOverIndex === null || dragIndex === dragOverIndex) {
            setDragIndex(null)
            setDragOverIndex(null)
            return
        }

        const newSections = [...sections]
        const [movedItem] = newSections.splice(dragIndex, 1)
        newSections.splice(dragOverIndex, 0, movedItem)

        const reorderedPayload = newSections.map((s, i) => ({ _id: s._id, order: i }))

        setSections(newSections)
        setDragIndex(null)
        setDragOverIndex(null)

        try {
            await HitApi({ sections: reorderedPayload }, reorderSections)
            toast.success('Order updated')
            onSectionsChange?.()
        } catch (error) {
            toast.error('Failed to reorder')
            fetchSections()
        }
    }

    const handleModalClose = (shouldRefresh = false) => {
        setShowModal(false)
        setEditingSection(null)
        if (shouldRefresh) {
            fetchServiceTypes()
            fetchSections()
            onSectionsChange?.()
        }
    }

    const getDisplayTypeIcon = (type) => {
        switch (type) {
            case 'horizontal_scroll': return <ArrowLeftRight className="w-4 h-4" />
            case 'grid': return <LayoutGrid className="w-4 h-4" />
            case 'list': return <List className="w-4 h-4" />
            default: return <ArrowLeftRight className="w-4 h-4" />
        }
    }

    const getDisplayTypeLabel = (type) => {
        switch (type) {
            case 'horizontal_scroll': return 'Horizontal Scroll'
            case 'grid': return 'Grid'
            case 'list': return 'List'
            default: return type
        }
    }

    const getGenderBadge = (gender) => {
        switch (gender) {
            case 'male':
                return <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700">Male</span>
            case 'female':
                return <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-pink-100 text-pink-700">Female</span>
            default:
                return <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700">All</span>
        }
    }

    if (!unitId) {
        return (
            <div className="text-center py-12">
                <p className="text-gray-500">Please select a unit first</p>
            </div>
        )
    }

    return (
        <div className="p-6">
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
                <div>
                    <h2 className="text-lg font-semibold text-gray-800">App Sections</h2>
                    <p className="text-sm text-gray-500 mt-1">
                        Create and manage custom sections for the client app home screen
                    </p>
                </div>
                <button
                    onClick={handleAddSection}
                    className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors"
                >
                    <Plus className="w-4 h-4" />
                    Add Section
                </button>
            </div>

            {/* Service Type Tabs */}
            <div className="flex gap-2 mb-6 flex-wrap">
                {serviceTypesLoading ? (
                    <div className="flex items-center gap-2 px-4 py-2 bg-gray-100 rounded-lg">
                        <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
                        <span className="text-sm text-gray-400">Loading service types...</span>
                    </div>
                ) : serviceTypes.length === 0 ? (
                    <div className="px-4 py-2 bg-gray-50 text-gray-500 rounded-lg text-sm">
                        No sections yet. Create a section to get started.
                    </div>
                ) : (
                    serviceTypes.map((st) => (
                        <button
                            key={st}
                            onClick={() => setServiceType(st)}
                            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors capitalize ${
                                serviceType === st
                                    ? 'bg-indigo-600 text-white shadow-sm'
                                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                            }`}
                        >
                            {st}
                        </button>
                    ))
                )}
            </div>

            {/* Loading */}
            {loading ? (
                <div className="flex items-center justify-center py-20">
                    <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
                </div>
            ) : sections.length === 0 ? (
                <div className="text-center py-20 bg-white rounded-xl border">
                    <LayoutGrid className="w-16 h-16 text-gray-200 mx-auto mb-4" />
                    <p className="text-gray-500 font-medium">No sections yet</p>
                    <p className="text-sm text-gray-400 mt-1">
                        Create sections like "Best Services", "Men's Special" to showcase services
                    </p>
                    <button
                        onClick={handleAddSection}
                        className="mt-4 inline-flex items-center gap-2 bg-blue-600 text-white px-6 py-2 rounded-lg text-sm font-medium hover:bg-blue-700"
                    >
                        <Plus className="w-4 h-4" />
                        Create Your First Section
                    </button>
                </div>
            ) : (
                <div className="space-y-3">
                    {sections.map((section, index) => (
                        <div
                            key={section._id}
                            draggable
                            onDragStart={(e) => handleDragStart(e, index)}
                            onDragOver={(e) => handleDragOver(e, index)}
                            onDragEnd={handleDragEnd}
                            className={`bg-white rounded-xl border overflow-hidden transition-all ${
                                section.isActive ? 'border-gray-200' : 'border-gray-100 opacity-60'
                            } ${dragIndex === index ? 'opacity-40 scale-[0.98]' : ''} ${
                                dragOverIndex === index && dragIndex !== null && dragIndex !== index
                                    ? 'border-blue-400 border-2 shadow-md'
                                    : ''
                            }`}
                        >
                            <div className="p-4">
                                <div className="flex items-center gap-4">
                                    {/* Drag Handle & Order */}
                                    <div className="flex flex-col items-center gap-1 cursor-grab active:cursor-grabbing">
                                        <GripVertical className="w-5 h-5 text-gray-300" />
                                        <div className="text-xs font-medium text-gray-400 w-6 text-center">
                                            {index + 1}
                                        </div>
                                    </div>

                                    {/* Section Info */}
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2">
                                            <h3 className="font-semibold text-gray-900">{section.title}</h3>
                                            {getGenderBadge(section.gender)}
                                        </div>
                                        <div className="flex items-center gap-4 mt-2 text-sm text-gray-500">
                                            <span className="flex items-center gap-1.5">
                                                {getDisplayTypeIcon(section.displayType)}
                                                {getDisplayTypeLabel(section.displayType)}
                                            </span>
                                            <span>
                                                {section.services?.length || 0} services
                                            </span>
                                            {section.description && (
                                                <span className="text-gray-400 truncate max-w-xs">
                                                    {section.description}
                                                </span>
                                            )}
                                        </div>

                                        {/* Service Previews */}
                                        {section.services?.length > 0 && (
                                            <div className="flex items-center gap-2 mt-3 overflow-x-auto pb-1">
                                                {section.services.slice(0, 5).map((s, i) => (
                                                    <div
                                                        key={i}
                                                        className="flex-shrink-0 px-2 py-1 bg-gray-100 rounded text-xs text-gray-600"
                                                    >
                                                        {s.serviceId?.name || 'Unknown'}
                                                    </div>
                                                ))}
                                                {section.services.length > 5 && (
                                                    <span className="text-xs text-gray-400">
                                                        +{section.services.length - 5} more
                                                    </span>
                                                )}
                                            </div>
                                        )}
                                    </div>

                                    {/* Actions */}
                                    <div className="flex items-center gap-2">
                                        <button
                                            onClick={() => handleToggleVisibility(section)}
                                            className={`p-2 rounded-lg transition-colors ${
                                                section.isActive
                                                    ? 'bg-green-100 text-green-600 hover:bg-green-200'
                                                    : 'bg-gray-100 text-gray-400 hover:bg-gray-200'
                                            }`}
                                            title={section.isActive ? 'Hide from app' : 'Show on app'}
                                        >
                                            {section.isActive ? (
                                                <Eye className="w-4 h-4" />
                                            ) : (
                                                <EyeOff className="w-4 h-4" />
                                            )}
                                        </button>
                                        <button
                                            onClick={() => handleEditSection(section)}
                                            disabled={editLoading === section._id}
                                            className="p-2 bg-blue-100 text-blue-600 rounded-lg hover:bg-blue-200 transition-colors disabled:opacity-50"
                                            title="Edit section"
                                        >
                                            {editLoading === section._id
                                                ? <Loader2 className="w-4 h-4 animate-spin" />
                                                : <Pencil className="w-4 h-4" />
                                            }
                                        </button>
                                        <button
                                            onClick={() => handleDeleteSection(section)}
                                            className="p-2 bg-red-100 text-red-600 rounded-lg hover:bg-red-200 transition-colors"
                                            title="Delete section"
                                        >
                                            <Trash2 className="w-4 h-4" />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Section Modal */}
            {showModal && (
                <SectionModal
                    section={editingSection}
                    parentServices={parentServices}
                    unitId={unitId}
                    projectId={projectId}
                    serviceType={serviceType}
                    onClose={handleModalClose}
                />
            )}
        </div>
    )
}

export default SectionsManager
